import React, { useState } from 'react';
import axios from 'axios';
import { Check, Loader2, Server, User, Building2, Mail, ChevronRight, Eye, EyeOff } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api/setup';

const STEPS = [
  { id: 'welcome', label: 'Benvenuto', icon: Server },
  { id: 'admin', label: 'Amministratore', icon: User },
  { id: 'property', label: 'Piattaforma', icon: Building2 },
  { id: 'smtp', label: 'Email', icon: Mail },
  { id: 'done', label: 'Completato', icon: Check },
];

export default function SetupWizard({ onComplete }) {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [form, setForm] = useState({
    admin_name: '',
    admin_email: '',
    admin_password: '',
    admin_password_confirm: '',
    app_name: '',
    app_name_en: '',
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    smtp_from: '',
  });

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const next = () => {
    setError('');
    if (step === 1) {
      if (!form.admin_name.trim()) return setError('Inserisci il nome dell\'amministratore.');
      if (!form.admin_email.trim()) return setError('Inserisci l\'email.');
      if (form.admin_password.length < 8) return setError('La password deve essere di almeno 8 caratteri.');
      if (form.admin_password !== form.admin_password_confirm) return setError('Le password non coincidono.');
    }
    if (step === 2) {
      if (!form.app_name.trim()) return setError('Inserisci il nome della piattaforma in italiano.');
    }
    setStep(s => s + 1);
  };

  const submit = async () => {
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/init`, {
        admin_name: form.admin_name,
        admin_email: form.admin_email,
        admin_password: form.admin_password,
        app_name: form.app_name,
        app_name_en: form.app_name_en || form.app_name,
        smtp_host: form.smtp_host,
        smtp_port: Number(form.smtp_port),
        smtp_user: form.smtp_user,
        smtp_password: form.smtp_password,
        smtp_from: form.smtp_from,
      });
      setStep(4);
    } catch (err) {
      const status = err.response?.status;
      // 403 = setup già completato in una sessione precedente.
      // Trattalo come successo: le credenziali del wizard non sono state salvate
      // (perché l'admin esiste già), ma il tenant è configurato. Vai al login.
      if (status === 403) {
        setStep(4);
        return;
      }
      setError(err.response?.data?.detail || 'Errore durante la configurazione.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--lux-bg)] flex flex-col items-center justify-center px-4">
      {/* Header */}
      <div className="w-full max-w-lg mb-8 text-center">
        <p className="text-[var(--lux-text-muted-2)] text-[10px] uppercase tracking-[0.4em] mb-2">
          Francesco Russo · francescorusso.work
        </p>
        <h1 className="font-serif text-4xl text-[var(--lux-text)] font-light">
          Installazione
        </h1>
        <p className="text-[var(--lux-text-muted)] text-sm mt-2">
          Smart Access & Booking Management Platform
        </p>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-0 mb-8">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          const active = step === i;
          const done = step > i;
          return (
            <div key={s.id} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs transition-all ${
                done ? 'bg-[var(--lux-gold)] text-black' :
                active ? 'bg-[var(--lux-surface-3)] border border-[var(--lux-gold)] text-[var(--lux-gold)]' :
                'bg-[var(--lux-surface-3)] text-[var(--lux-text-muted-3)]'
              }`}>
                {done ? <Check size={12} strokeWidth={2.5} /> : <Icon size={12} strokeWidth={1.5} />}
              </div>
              {i < STEPS.length - 1 && (
                <div className={`w-8 h-px ${done ? 'bg-[var(--lux-gold)]' : 'bg-[var(--lux-border-2)]'}`} />
              )}
            </div>
          );
        })}
      </div>

      {/* Card */}
      <div className="w-full max-w-lg bg-[var(--lux-surface-1)] border border-[var(--lux-border-1)] rounded-2xl p-8">

        {/* Step 0: Welcome */}
        {step === 0 && (
          <div className="space-y-6">
            <div>
              <p className="text-[var(--lux-gold)] text-[10px] uppercase tracking-widest mb-2">Primo avvio</p>
              <h2 className="font-serif text-2xl text-[var(--lux-text)] font-light">Benvenuto</h2>
              <p className="text-[var(--lux-text-muted)] text-sm mt-3 leading-relaxed">
                Questa procedura guidata configura la piattaforma per il tuo cliente.
                Creerai l'account amministratore principale e le impostazioni di base.
              </p>
            </div>
            <div className="bg-[var(--lux-surface-3)] rounded-xl p-4 space-y-2">
              {['Creazione account super admin', 'Configurazione nome piattaforma', 'Impostazioni email SMTP (opzionale)'].map((t, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-[var(--lux-gold)]/10 flex items-center justify-center shrink-0">
                    <Check size={10} className="text-[var(--lux-gold)]" />
                  </div>
                  <p className="text-[var(--lux-text-muted)] text-sm">{t}</p>
                </div>
              ))}
            </div>
            <button onClick={next} className="w-full bg-[var(--lux-gold)] text-black rounded-xl py-3.5 font-medium flex items-center justify-center gap-2">
              Inizia configurazione <ChevronRight size={16} />
            </button>
          </div>
        )}

        {/* Step 1: Admin account */}
        {step === 1 && (
          <div className="space-y-5">
            <div>
              <p className="text-[var(--lux-gold)] text-[10px] uppercase tracking-widest mb-2">Passo 1 di 3</p>
              <h2 className="font-serif text-2xl text-[var(--lux-text)] font-light">Account Amministratore</h2>
              <p className="text-[var(--lux-text-muted)] text-sm mt-1">Crea le credenziali di accesso al pannello di controllo.</p>
            </div>
            <div className="space-y-4">
              <Field label="Nome completo" value={form.admin_name} onChange={v => set('admin_name', v)} placeholder="Mario Rossi" />
              <Field label="Email" type="email" value={form.admin_email} onChange={v => set('admin_email', v)} placeholder="admin@tuodominio.com" />
              <div>
                <label className="block text-[var(--lux-text-muted-2)] text-xs uppercase tracking-widest mb-1.5">Password</label>
                <div className="relative">
                  <input
                    type={showPwd ? 'text' : 'password'}
                    value={form.admin_password}
                    onChange={e => set('admin_password', e.target.value)}
                    placeholder="Minimo 8 caratteri"
                    className="w-full bg-[var(--lux-surface-3)] border border-[var(--lux-border-2)] rounded-lg px-4 py-3 text-[var(--lux-text)] text-sm focus:outline-none focus:border-[var(--lux-gold)] pr-10"
                  />
                  <button onClick={() => setShowPwd(p => !p)} className="absolute right-3 top-3 text-[var(--lux-text-muted-2)]">
                    {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <Field label="Conferma password" type="password" value={form.admin_password_confirm} onChange={v => set('admin_password_confirm', v)} placeholder="Ripeti la password" />
            </div>
            {error && <p className="text-red-400 text-sm">{error}</p>}
            <div className="flex gap-3">
              <button onClick={() => setStep(s => s - 1)} className="flex-1 border border-[var(--lux-border-2)] text-[var(--lux-text-muted)] rounded-xl py-3 text-sm">Indietro</button>
              <button onClick={next} className="flex-1 bg-[var(--lux-gold)] text-black rounded-xl py-3 font-medium">Continua</button>
            </div>
          </div>
        )}

        {/* Step 2: Platform name */}
        {step === 2 && (
          <div className="space-y-5">
            <div>
              <p className="text-[var(--lux-gold)] text-[10px] uppercase tracking-widest mb-2">Passo 2 di 3</p>
              <h2 className="font-serif text-2xl text-[var(--lux-text)] font-light">Nome Piattaforma</h2>
              <p className="text-[var(--lux-text-muted)] text-sm mt-1">Il nome sarà mostrato in tutta l'app e nelle email.</p>
            </div>
            <div className="space-y-4">
              <Field label="Nome in italiano" value={form.app_name} onChange={v => set('app_name', v)} placeholder="es. Villa Belvedere" />
              <Field label="Nome in inglese" value={form.app_name_en} onChange={v => set('app_name_en', v)} placeholder="es. Villa Belvedere" />
            </div>
            {error && <p className="text-red-400 text-sm">{error}</p>}
            <div className="flex gap-3">
              <button onClick={() => setStep(s => s - 1)} className="flex-1 border border-[var(--lux-border-2)] text-[var(--lux-text-muted)] rounded-xl py-3 text-sm">Indietro</button>
              <button onClick={next} className="flex-1 bg-[var(--lux-gold)] text-black rounded-xl py-3 font-medium">Continua</button>
            </div>
          </div>
        )}

        {/* Step 3: SMTP */}
        {step === 3 && (
          <div className="space-y-5">
            <div>
              <p className="text-[var(--lux-gold)] text-[10px] uppercase tracking-widest mb-2">Passo 3 di 3 — Opzionale</p>
              <h2 className="font-serif text-2xl text-[var(--lux-text)] font-light">Configurazione Email</h2>
              <p className="text-[var(--lux-text-muted)] text-sm mt-1">
                Configura il server SMTP per inviare email agli ospiti. Puoi farlo anche in seguito dalle Impostazioni.
              </p>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <Field label="Server SMTP" value={form.smtp_host} onChange={v => set('smtp_host', v)} placeholder="smtp.gmail.com" />
                </div>
                <Field label="Porta" type="number" value={form.smtp_port} onChange={v => set('smtp_port', v)} placeholder="587" />
              </div>
              <Field label="Username / Email mittente" value={form.smtp_user} onChange={v => set('smtp_user', v)} placeholder="noreply@tuodominio.com" />
              <Field label="Password SMTP" type="password" value={form.smtp_password} onChange={v => set('smtp_password', v)} placeholder="App password" />
            </div>
            {error && <p className="text-red-400 text-sm">{error}</p>}
            <div className="flex gap-3">
              <button onClick={() => setStep(s => s - 1)} className="flex-1 border border-[var(--lux-border-2)] text-[var(--lux-text-muted)] rounded-xl py-3 text-sm">Indietro</button>
              <button onClick={submit} disabled={loading}
                className="flex-1 bg-[var(--lux-gold)] text-black rounded-xl py-3 font-medium flex items-center justify-center gap-2">
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                Completa installazione
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Done */}
        {step === 4 && (
          <div className="text-center space-y-6 py-4">
            <div className="w-16 h-16 bg-[var(--lux-gold)]/10 rounded-full flex items-center justify-center mx-auto">
              <Check size={28} className="text-[var(--lux-gold)]" strokeWidth={1.5} />
            </div>
            <div>
              <h2 className="font-serif text-2xl text-[var(--lux-text)] font-light">Installazione Completata</h2>
              <p className="text-[var(--lux-text-muted)] text-sm mt-2">
                La piattaforma è pronta. Accedi con le credenziali che hai appena creato.
              </p>
            </div>
            <div className="bg-[var(--lux-surface-3)] rounded-xl p-4 text-left space-y-2">
              <p className="text-[var(--lux-text-muted-2)] text-xs uppercase tracking-widest mb-1">Le tue credenziali</p>
              <p className="text-[var(--lux-text)] text-sm">Email: <span className="text-[var(--lux-gold)]">{form.admin_email}</span></p>
              <p className="text-[var(--lux-text-muted)] text-xs">Salva queste credenziali in un posto sicuro.</p>
            </div>
            <button onClick={() => onComplete?.()}
              className="w-full bg-[var(--lux-gold)] text-black rounded-xl py-3.5 font-medium">
              Vai al pannello admin
            </button>
          </div>
        )}
      </div>

      {/* Footer */}
      <p className="text-[var(--lux-text-muted-3)] text-[10px] mt-6 text-center">
        Sviluppato da Francesco Russo ·{' '}
        <a href="https://francescorusso.work" target="_blank" rel="noreferrer" className="underline">
          francescorusso.work
        </a>
      </p>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', placeholder }) {
  return (
    <div>
      <label className="block text-[var(--lux-text-muted-2)] text-xs uppercase tracking-widest mb-1.5">{label}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-[var(--lux-surface-3)] border border-[var(--lux-border-2)] rounded-lg px-4 py-3 text-[var(--lux-text)] text-sm placeholder-[var(--lux-text-muted-3)] focus:outline-none focus:border-[var(--lux-gold)]"
      />
    </div>
  );
}
